import React, { useState, useMemo, useCallback } from 'react';
import { MainLayout, Button, Form, FilterButton } from '@app/components';
import { CustomerHeader } from '@app/features/CustomerHeader';
import { useForm, Controller } from 'react-hook-form';
import styled from '@2pulse/responsive';
import { row, col } from '@app/style';
import { normalize } from '@app/helpers';
import { customerApi } from '@app/store';
import { DateTimePicker } from '@app/components/form/DateTimePicker.web';
import { ShopSelect } from '@app/components/form/selects/features/ShopSelect';
import { theme } from '@app/theme';
import { DateTime } from 'luxon';
import { IShop } from '@app/common';
import { View, Text } from 'react-native';

interface ExportFilters {
  shops: IShop[];
  optInSMS: string;
  optInEmail: string;
  optInLoyalty: string;
  ageRange: string;
  registeredSince: string | null;
  lastOrderFrom: string | null;
  lastOrderTo: string | null;
  minOrdersCount: string;
  minCA: string;
  registeredFrom: string;
}

const OPT_IN_OPTIONS = [
  { label: 'Tous', value: '' },
  { label: 'Oui', value: 'true' },
  { label: 'Non', value: 'false' },
];

const AGE_RANGE_OPTIONS = [
  { label: 'Tous les âges', value: '' },
  { label: '18-25 ans', value: '18-25' },
  { label: '25-35 ans', value: '25-35' },
  { label: '35-45 ans', value: '35-45' },
  { label: '45-55 ans', value: '45-55' },
  { label: '55+ ans', value: '55-999' },
];

const SOURCE_OPTIONS = [
  { label: 'Toutes sources', value: '' },
  { label: 'Application mobile', value: 'app' },
  { label: 'Site web', value: 'web' },
  { label: 'Caisse', value: 'store' },
];

export const CustomerExportScreen = () => {
  const [exportCsv, { isLoading: isExporting }] = customerApi.useExportCsvMutation();
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const formMethods = useForm<ExportFilters>({
    defaultValues: {
      shops: [],
      optInSMS: '',
      optInEmail: '',
      optInLoyalty: '',
      ageRange: '',
      registeredSince: null,
      lastOrderFrom: null,
      lastOrderTo: null,
      minOrdersCount: '',
      minCA: '',
      registeredFrom: '',
    }
  });

  const watchedValues = formMethods.watch();

  const buildExportParams = useCallback(() => {
    const params: any = {};

    // Magasins
    if (watchedValues.shops && watchedValues.shops.length > 0) {
      params.shopIds = watchedValues.shops.map((s: IShop) => s._id);
    }

    // Opt-ins
    if (watchedValues.optInSMS !== '') {
      params.optInSMS = watchedValues.optInSMS === 'true';
    }
    if (watchedValues.optInEmail !== '') {
      params.optInEmail = watchedValues.optInEmail === 'true';
    }
    if (watchedValues.optInLoyalty !== '') {
      params.optInLoyalty = watchedValues.optInLoyalty === 'true';
    }

    // Tranche d'âge
    if (watchedValues.ageRange) {
      const [min, max] = watchedValues.ageRange.split('-').map(Number);
      params.ageMin = min;
      params.ageMax = max;
    }

    // Date d'inscription
    if (watchedValues.registeredSince) {
      params.registeredSince = watchedValues.registeredSince;
    }

    // Dernière commande
    if (watchedValues.lastOrderFrom) {
      params.lastOrderFrom = watchedValues.lastOrderFrom;
    }
    if (watchedValues.lastOrderTo) {
      params.lastOrderTo = watchedValues.lastOrderTo;
    }

    // Nombre de commandes minimum
    if (watchedValues.minOrdersCount && !isNaN(Number(watchedValues.minOrdersCount))) {
      params.minOrdersCount = Number(watchedValues.minOrdersCount);
    }

    // CA minimum
    if (watchedValues.minCA && !isNaN(Number(watchedValues.minCA))) {
      params.minCA = Number(watchedValues.minCA);
    }

    // Source d'inscription
    if (watchedValues.registeredFrom) {
      params.registeredFrom = watchedValues.registeredFrom;
    }

    return params;
  }, [watchedValues]);

  const handleExport = async () => {
    try {
      const params = buildExportParams();
      const result = await exportCsv(params).unwrap();
      
      if (result.csv) {
        // Décoder le base64 et télécharger
        const base64 = result.csv.split(',')[1];
        const binary = atob(base64);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        const today = DateTime.now().toFormat('dd-MM-yyyy');
        a.download = `Export_clients_${today}.csv`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 0);

        setPreviewCount(result.count);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const resetFilters = () => {
    formMethods.reset();
    setPreviewCount(null);
  };

  // Compter les filtres actifs
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (watchedValues.shops?.length > 0) count++;
    if (watchedValues.optInSMS !== '') count++;
    if (watchedValues.optInEmail !== '') count++;
    if (watchedValues.optInLoyalty !== '') count++;
    if (watchedValues.ageRange !== '') count++;
    if (watchedValues.registeredSince) count++;
    if (watchedValues.lastOrderFrom || watchedValues.lastOrderTo) count++;
    if (watchedValues.minOrdersCount) count++;
    if (watchedValues.minCA) count++;
    if (watchedValues.registeredFrom !== '') count++;
    return count;
  }, [watchedValues]);

  return (
    <MainLayout
      header={(
        <CustomerHeader />
      )}>
      <Container>
        <Form formMethods={formMethods}>
          <Title>Export des clients</Title>
          <Subtitle>Configurez vos filtres pour exporter une liste de clients au format CSV</Subtitle>

          <FiltersGrid>
            {/* Magasins */}
            <FilterSection>
              <FilterTitle>Magasins</FilterTitle>
              <ShopSelect
                name="shops"
                label="Sélectionner les magasins"
                onWhite
                isMultiple
              />
            </FilterSection>

            {/* Opt-ins */}
            <FilterSection>
              <FilterTitle>Préférences de communication</FilterTitle>
              <FilterRow>
                <SelectWrapper>
                  <SelectLabel>Opt-in SMS</SelectLabel>
                  <Controller
                    name="optInSMS"
                    control={formMethods.control}
                    render={({ field }) => (
                      <NativeSelect
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        {OPT_IN_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </NativeSelect>
                    )}
                  />
                </SelectWrapper>
                <SelectWrapper>
                  <SelectLabel>Opt-in Email</SelectLabel>
                  <Controller
                    name="optInEmail"
                    control={formMethods.control}
                    render={({ field }) => (
                      <NativeSelect
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        {OPT_IN_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </NativeSelect>
                    )}
                  />
                </SelectWrapper>
                <SelectWrapper>
                  <SelectLabel>Opt-in Fidélité</SelectLabel>
                  <Controller
                    name="optInLoyalty"
                    control={formMethods.control}
                    render={({ field }) => (
                      <NativeSelect
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        {OPT_IN_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </NativeSelect>
                    )}
                  />
                </SelectWrapper>
              </FilterRow>
            </FilterSection>

            {/* Tranche d'âge */}
            <FilterSection>
              <FilterTitle>Tranche d'âge</FilterTitle>
              <AgeButtonsContainer>
                {AGE_RANGE_OPTIONS.map(option => (
                  <AgeButton
                    key={option.value}
                    active={watchedValues.ageRange === option.value}
                    onPress={() => formMethods.setValue('ageRange', option.value)}
                  >
                    <AgeButtonText active={watchedValues.ageRange === option.value}>
                      {option.label}
                    </AgeButtonText>
                  </AgeButton>
                ))}
              </AgeButtonsContainer>
            </FilterSection>

            {/* Dates */}
            <FilterSection>
              <FilterTitle>Période d'inscription</FilterTitle>
              <FilterRow>
                <DateTimePicker
                  name="registeredSince"
                  type="date"
                  label="Inscrit depuis"
                  onWhite
                />
              </FilterRow>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Dernière commande</FilterTitle>
              <FilterRow>
                <DateTimePicker
                  name="lastOrderFrom"
                  type="date"
                  label="Du"
                  onWhite
                />
                <DateTimePicker
                  name="lastOrderTo"
                  type="date"
                  label="Au"
                  onWhite
                />
              </FilterRow>
            </FilterSection>

            {/* Activité */}
            <FilterSection>
              <FilterTitle>Activité client</FilterTitle>
              <FilterRow>
                <InputWrapper>
                  <InputLabel>Nb commandes min.</InputLabel>
                  <StyledInput
                    value={watchedValues.minOrdersCount}
                    onChangeText={(text) => formMethods.setValue('minOrdersCount', text)}
                    placeholder="Ex: 5"
                    keyboardType="numeric"
                  />
                </InputWrapper>
                <InputWrapper>
                  <InputLabel>CA minimum (€)</InputLabel>
                  <StyledInput
                    value={watchedValues.minCA}
                    onChangeText={(text) => formMethods.setValue('minCA', text)}
                    placeholder="Ex: 100"
                    keyboardType="numeric"
                  />
                </InputWrapper>
              </FilterRow>
            </FilterSection>

            {/* Source */}
            <FilterSection>
              <FilterTitle>Source d'inscription</FilterTitle>
              <SelectWrapper style={{ maxWidth: 300 }}>
                <Controller
                  name="registeredFrom"
                  control={formMethods.control}
                  render={({ field }) => (
                    <NativeSelect
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {SOURCE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </NativeSelect>
                  )}
                />
              </SelectWrapper>
            </FilterSection>
          </FiltersGrid>

          {/* Actions */}
          <ActionsContainer>
            <FiltersInfo>
              {activeFiltersCount > 0 ? (
                <FiltersInfoText>{activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif{activeFiltersCount > 1 ? 's' : ''}</FiltersInfoText>
              ) : (
                <FiltersInfoText>Aucun filtre - Tous les clients seront exportés</FiltersInfoText>
              )}
              {previewCount !== null && (
                <ResultCount>{previewCount} client{previewCount > 1 ? 's' : ''} exporté{previewCount > 1 ? 's' : ''}</ResultCount>
              )}
            </FiltersInfo>
            <ButtonsRow>
              <FilterButton
                title="Réinitialiser"
                onPress={resetFilters}
              />
              <Button
                title="Exporter en CSV"
                onPress={handleExport}
                loading={isExporting}
                disabled={isExporting}
              />
            </ButtonsRow>
          </ActionsContainer>
        </Form>
      </Container>
    </MainLayout>
  );
};

const Container = styled.ScrollView(({ theme }) => ({
  flex: 1,
  padding: normalize(30),
  paddingLeft: theme.sizes.sidebarWidth + normalize(30),
}));

const Title = styled.Text(({ theme }) => ({
  fontSize: normalize(24),
  fontWeight: '700',
  color: theme.colors.oxfordblue,
  marginBottom: normalize(8),
}));

const Subtitle = styled.Text(({ theme }) => ({
  fontSize: normalize(14),
  color: theme.colors.oxfordblue,
  opacity: 0.7,
  marginBottom: normalize(30),
}));

const FiltersGrid = styled.View({
  ...col,
  gap: normalize(24),
});

const FilterSection = styled.View({
  backgroundColor: '#FFF',
  borderRadius: normalize(12),
  padding: normalize(20),
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.05,
  shadowRadius: 8,
  elevation: 2,
});

const FilterTitle = styled.Text(({ theme }) => ({
  fontSize: normalize(14),
  fontWeight: '600',
  color: theme.colors.oxfordblue,
  marginBottom: normalize(16),
}));

const FilterRow = styled.View({
  ...row,
  gap: normalize(16),
  flexWrap: 'wrap',
});

const SelectWrapper = styled.View({
  flex: 1,
  minWidth: normalize(150),
});

const SelectLabel = styled.Text(({ theme }) => ({
  fontSize: normalize(12),
  fontWeight: '500',
  color: theme.colors.oxfordblue,
  marginBottom: normalize(8),
}));

// Composant select natif HTML
const NativeSelect = ({ value, onChange, children, style }: { 
  value: string; 
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; 
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <select
    value={value}
    onChange={onChange}
    style={{
      width: '100%',
      height: normalize(44),
      border: '1px solid #E5E7EB',
      borderRadius: normalize(8),
      paddingLeft: normalize(12),
      paddingRight: normalize(32),
      fontSize: normalize(14),
      color: '#0B2032',
      backgroundColor: '#FFF',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 8px center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '20px',
      outline: 'none',
      ...style,
    }}
  >
    {children}
  </select>
);

const AgeButtonsContainer = styled.View({
  ...row,
  gap: normalize(8),
  flexWrap: 'wrap',
});

const AgeButton = styled.TouchableOpacity<{ active?: boolean }>(({ theme, props }) => ({
  paddingVertical: normalize(10),
  paddingHorizontal: normalize(16),
  borderRadius: normalize(8),
  backgroundColor: props.active ? theme.colors.celadonblue : theme.colors.celadonblue + '15',
  borderWidth: 1,
  borderColor: props.active ? theme.colors.celadonblue : 'transparent',
}));

const AgeButtonText = styled.Text<{ active?: boolean }>(({ theme, props }) => ({
  fontSize: normalize(13),
  fontWeight: '500',
  color: props.active ? '#FFF' : theme.colors.oxfordblue,
}));

const InputWrapper = styled.View({
  flex: 1,
  minWidth: normalize(150),
});

const InputLabel = styled.Text(({ theme }) => ({
  fontSize: normalize(12),
  fontWeight: '500',
  color: theme.colors.oxfordblue,
  marginBottom: normalize(8),
}));

const StyledInput = styled.TextInput(({ theme }) => ({
  height: normalize(44),
  borderWidth: 1,
  borderColor: '#E5E7EB',
  borderRadius: normalize(8),
  paddingHorizontal: normalize(12),
  fontSize: normalize(14),
  color: theme.colors.oxfordblue,
  backgroundColor: '#FFF',
}));

const ActionsContainer = styled.View({
  ...row,
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: normalize(30),
  paddingTop: normalize(20),
  borderTopWidth: 1,
  borderTopColor: '#E5E7EB',
  flexWrap: 'wrap',
  gap: normalize(16),
});

const FiltersInfo = styled.View({
  ...col,
  gap: normalize(4),
});

const FiltersInfoText = styled.Text(({ theme }) => ({
  fontSize: normalize(13),
  color: theme.colors.oxfordblue,
  opacity: 0.7,
}));

const ResultCount = styled.Text(({ theme }) => ({
  fontSize: normalize(14),
  fontWeight: '600',
  color: theme.colors.celadonblue,
}));

const ButtonsRow = styled.View({
  ...row,
  gap: normalize(12),
});
